// ---------------------------------------------------------------------------
// Contribution Period option rules
// ---------------------------------------------------------------------------
// Single source of truth for the "Contribution Period" dropdown: the list of
// options, their i18n labels, and the rule that computes each option's
// start (beginningYear) and end (endYear) dates.
//
// HOW TO ADD OR CHANGE A CONTRIBUTION PERIOD OPTION:
//   1. (New option only) add a key to CONTRIBUTION_PERIOD_KEYS below.
//   2. Add / edit an entry in CONTRIBUTION_PERIOD_OPTIONS:
//        - labelKey : the i18n key rendered as the dropdown label.
//        - getStart / getEnd : pure functions that receive the resolved
//          ContributionPeriodDateContext (dates already computed for the
//          selected account holder) and return an ISO date string (YYYY-MM-DD).
//        - Omit getStart / getEnd to make the option behave like CUSTOM
//          (blank date pickers the user fills in manually).
//   3. If an option needs a date building-block that does not exist yet
//      (e.g. "retirement + 10 years"), add the field to
//      ContributionPeriodDateContext and resolve it wherever the context is
//      built (see AccountsInlineTable).
//
// The builders only ever receive already-resolved ISO dates (never raw
// ages / DOBs), so this file stays free of Redux / holder logic and is easy to
// unit test.
// ---------------------------------------------------------------------------

export const CONTRIBUTION_PERIOD_KEYS = {
    NOW_TO_RETIREMENT: 'NOW_TO_RETIREMENT',
    FULL_PLANNING_PERIOD: 'FULL_PLANNING_PERIOD',
    RETIREMENT_PERIOD: 'RETIREMENT_PERIOD',
    CUSTOM: 'CUSTOM'
} as const;

export const IncomeSorceDefaultPeriodMapping: Record<string, string> = {
    'Salary & Compensation​': CONTRIBUTION_PERIOD_KEYS.NOW_TO_RETIREMENT,
    'Business Income': CONTRIBUTION_PERIOD_KEYS.FULL_PLANNING_PERIOD,
    'Interest Income': CONTRIBUTION_PERIOD_KEYS.FULL_PLANNING_PERIOD,
    'Rental Income': CONTRIBUTION_PERIOD_KEYS.FULL_PLANNING_PERIOD,
    'Pension Income': CONTRIBUTION_PERIOD_KEYS.RETIREMENT_PERIOD,
    Inheritance: CONTRIBUTION_PERIOD_KEYS.CUSTOM,
    'Other Income': CONTRIBUTION_PERIOD_KEYS.CUSTOM
};

export const CONTRIBUTION_FREQUENCY = {
    MONTHLY: 'monthly',
    ANNUALLY: 'annually',
    ONE_TIME: 'onetime'
} as const;

export const IncomeSourceDefaultFrequencyMapping: Record<string, string> = {
    Inheritance: CONTRIBUTION_FREQUENCY.ONE_TIME
};
export type ContributionPeriodDates = { start: string; end: string };

// Resolved date building-blocks for the *currently selected account holder*.
// Add new building-blocks here when a new option needs a date we don't compute yet.
export interface ContributionPeriodDateContext {
    currentMonthStart: string; // 1st of the current month
    retirementDate: string; // holder's retirement date
    planningDate: string; // holder's planning date
}

export interface ContributionPeriodOptionConfig {
    key: string;
    labelKey: string;
    // eslint-disable-next-line no-unused-vars
    getStart?: (ctx: ContributionPeriodDateContext) => string;
    // eslint-disable-next-line no-unused-vars
    getEnd?: (ctx: ContributionPeriodDateContext) => string;
}

// The dropdown options, in display order. Edit here to add/reorder/retune options.
export const CONTRIBUTION_PERIOD_OPTIONS: ContributionPeriodOptionConfig[] = [
    {
        key: CONTRIBUTION_PERIOD_KEYS.NOW_TO_RETIREMENT,
        labelKey: 'PERIOD_NOW_TO_RETIREMENT',
        getStart: (ctx) => ctx.currentMonthStart,
        getEnd: (ctx) => ctx.retirementDate
    },
    {
        key: CONTRIBUTION_PERIOD_KEYS.FULL_PLANNING_PERIOD,
        labelKey: 'PERIOD_FULL_PLANNING_PERIOD',
        getStart: (ctx) => ctx.currentMonthStart,
        getEnd: (ctx) => ctx.planningDate
    },
    {
        key: CONTRIBUTION_PERIOD_KEYS.RETIREMENT_PERIOD,
        labelKey: 'PERIOD_RETIREMENT_PERIOD',
        getStart: (ctx) => ctx.retirementDate,
        getEnd: (ctx) => ctx.planningDate
    },
    {
        // No getStart / getEnd => blank pickers the user fills in manually.
        key: CONTRIBUTION_PERIOD_KEYS.CUSTOM,
        labelKey: 'PERIOD_CUSTOM'
    }
];

const findConfig = (key: string): ContributionPeriodOptionConfig | undefined =>
    CONTRIBUTION_PERIOD_OPTIONS.find((option) => option.key === key);

// Compute the { start, end } ISO dates for a given period key using its rule.
// Options without builders (e.g. CUSTOM) resolve to blank dates.
export const getContributionPeriodDates = (
    key: string,
    ctx: ContributionPeriodDateContext
): ContributionPeriodDates => {
    const config = findConfig(key);
    if (!config?.getStart || !config?.getEnd) return { start: '', end: '' };
    return { start: config.getStart(ctx), end: config.getEnd(ctx) };
};

// Reverse lookup: given concrete start/end dates, figure out which preset (if any)
// they match for the selected holder; fall back to CUSTOM.
export const deriveContributionPeriodKey = (start: string, end: string, ctx: ContributionPeriodDateContext): string => {
    if (!start || !end) return CONTRIBUTION_PERIOD_KEYS.CUSTOM;
    const match = CONTRIBUTION_PERIOD_OPTIONS.find(
        (option) => option.getStart && option.getEnd && option.getStart(ctx) === start && option.getEnd(ctx) === end
    );
    return match?.key ?? CONTRIBUTION_PERIOD_KEYS.CUSTOM;
};

// Resolve the i18n label key for a given period key (used for read-only display).
export const getContributionPeriodLabelKey = (key: string): string =>
    findConfig(key)?.labelKey ?? CONTRIBUTION_PERIOD_OPTIONS[CONTRIBUTION_PERIOD_OPTIONS.length - 1].labelKey;
